// Iter43-A — /tms Transport workspace
//
// Single page with two tabs: "Outgoing" (shipper view — TRs I created)
// and "Incoming" (carrier view — TRs sent to my tenant).
//
// Outgoing tab cards:
//   - Status badge, offers count, awarded offer summary
//   - "Compare offers →" expands an offers comparison table with Award btns
//   - "Cancel request" action
//
// Incoming tab cards (carrier):
//   - Status, agreed timing
//   - "Submit offer" form (rate + pickup time + transit minutes + delivery)
//   - "Decline" action
//   - When awarded: vehicle-data form + countdown timer
//   - Pickup/delivery confirm buttons in sequence
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { hasModuleAccess } from "../lib/moduleAccess";
import { useStore } from "../store";
import { CreateTrModal } from "../components/TmsSection";
import { UniversalFilter } from "../components/filters/UniversalFilter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLOR = {
  requested: "#9ec3ec",
  offers_collected: "#c89adb",
  agreed: "#f3b14a",
  dispatched: "#f3b14a",
  in_transit: "#5fa3e3",
  delivered: "#4ed084",
  cancelled: "#9aa1b3",
};

// Iter43-B — render a structured location object as a single line.
// Falls back gracefully to a plain string for legacy docs.
const formatAddr = (loc) => {
  if (!loc) return "—";
  if (typeof loc === "string") return loc;
  const street = [loc.street, loc.house_number].filter(Boolean).join(" ");
  const city = [loc.postal_code, loc.city].filter(Boolean).join(" ");
  const main = [street, city].filter(Boolean).join(", ");
  const gates = [];
  if (loc.entry_gate) gates.push(`Gate ${loc.entry_gate}`);
  if (loc.dock_gate) gates.push(`Dock ${loc.dock_gate}`);
  return main ? `${main}${gates.length ? " · " + gates.join(" · ") : ""}` : (gates.join(" · ") || "—");
};

export default function TMS() {
  const { user } = useAuth();
  const addToast = useStore((s) => s.addToast);
  const [tab, setTab] = useState("outgoing");
  const [out, setOut] = useState({ items: [], counts: {} });
  const [inc, setInc] = useState({ items: [], counts: {} });
  const [showCreate, setShowCreate] = useState(false);

  const refresh = async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/tms/my-requests`),
        axios.get(`${API}/tms/incoming-requests`),
      ]);
      setOut(a.data);
      setInc(b.data);
    } catch (err) {
      addToast({ type: "error", title: "TMS workspace failed", body: err.message });
    }
  };

  useEffect(() => {
    refresh();
    // Iter44 — auto-open create modal when arriving from the Service Requests
    // hub with `?new=1`.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("new") === "1") setShowCreate(true);
    } catch (_) { /* swallow */ }
  }, []);

  // Iter51 — universal filter wiring (TMS workspace). Hooks must precede any
  // early return below — these reference `tab`, `out`, `inc` already declared.
  const list = tab === "outgoing" ? out.items : inc.items;
  const counts = tab === "outgoing" ? out.counts : inc.counts;
  const trChips = useMemo(() => [
    { key: "status_chip", label: "Status", type: "select", options:
      Object.keys(counts || {}).filter((k) => k !== "all").map((k) => ({
        value: k,
        label: k.replace(/_/g, " "),
        predicate: (i) => i.status === k,
      })),
    },
    { key: "type_chip", label: "Type", type: "select", options: [
      { value: "standard", label: "Standard", predicate: (i) => i.type === "standard" },
      { value: "sonderfahrt", label: "Sonderfahrt", predicate: (i) => i.type === "sonderfahrt" },
    ]},
    { key: "link_chip", label: "Link", type: "select", options: [
      { value: "linked", label: "Linked to incident", predicate: (i) => Boolean(i.incident_id) },
      { value: "standalone", label: "Standalone", predicate: (i) => !i.incident_id },
    ]},
    { key: "date_chip", label: "Created", type: "dateRange", field: "created_at" },
  ], [counts]);

  const trSearchText = useCallback((i) => {
    const pu = i.pickup_location || {};
    const dl = i.delivery_location || {};
    const route = [pu.city, pu.street, dl.city, dl.street].filter(Boolean).join(" ");
    return [i.id, i.cargo_description, i.incident_id, i.notes, route, i.carrier_name].filter(Boolean).join(" ");
  }, []);

  if (!hasModuleAccess(user, "tms")) {
    return <div className="p-6" style={mono(11)}>No TMS access.</div>;
  }

  return (
    <div data-testid="tms-workspace">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: "clamp(26px, 3.4vw, 34px)", color: "var(--text, #f0f2f8)", letterSpacing: "-0.5px" }} data-testid="view-title">
            Transport · TMS
          </h1>
          <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
            Multi-carrier offers · system-recommended award · live execution
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="px-3 py-2 rounded"
          style={{ background: "#185FA5", color: "#fff", ...mono(11, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
          data-testid="tms-new-btn"
        >+ New transport request</button>
      </header>

      {/* Tabs */}
      <div className="flex gap-2 mb-4" data-testid="tms-tabs">
        <Tab active={tab === "outgoing"} onClick={() => setTab("outgoing")} testId="tms-tab-outgoing"
          label={`Outgoing · ${out.counts?.all || 0}`} />
        <Tab active={tab === "incoming"} onClick={() => setTab("incoming")} testId="tms-tab-incoming"
          label={`Incoming · ${inc.counts?.all || 0}`} />
      </div>

      {/* Status filter counts hint */}
      <div className="mb-3 flex flex-wrap gap-x-3" style={mono(9, "#9198b0", 500, "0.5px")}>
        {Object.entries(counts || {}).filter(([k]) => k !== "all").map(([k, v]) => (
          <span key={k} data-testid={`tms-count-${k}`}>{k.replace("_", " ")}: {v}</span>
        ))}
      </div>

      {list.length === 0 ? (
        <Empty
          text={tab === "outgoing"
            ? "No transport requests yet. Send your first one to receive carrier offers."
            : "No incoming transport requests at the moment."}
          hint={tab === "outgoing" ? "Tip: type 'standard' uses contracted rates when available; 'sonderfahrt' fans out to all selected carriers." : null}
          ctaLabel={tab === "outgoing" ? "+ New transport request" : null}
          ctaOnClick={() => setShowCreate(true)}
          testId="tms-empty"
        />
      ) : (
        <UniversalFilter
          storageKey={`tms_${tab}`}
          items={list}
          getSearchText={trSearchText}
          chips={trChips}
          placeholder="Search TR id, route, cargo, incident, carrier…"
          testIdPrefix="tms-uf"
          emptyMessage="No transport requests match the current filter."
        >
          {({ filtered }) => (
            <div className="space-y-3" data-testid="tms-list">
              {filtered.map((tr) => (
                <TrCard
                  key={tr.id}
                  tr={tr}
                  role={tab}
                  onChange={refresh}
                  addToast={addToast}
                />
              ))}
            </div>
          )}
        </UniversalFilter>
      )}

      {showCreate && (
        <CreateTrModal
          incident={null}
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await refresh(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TR card
// ---------------------------------------------------------------------------

const TrCard = ({ tr, role, onChange, addToast }) => {
  const [expand, setExpand] = useState(false);
  const isOutgoing = role === "outgoing";
  return (
    <div
      className="rounded-md p-3"
      style={{
        background: "var(--bg-surface, #0d0f18)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderLeft: `3px solid ${STATUS_COLOR[tr.status] || "#9aa1b3"}`,
      }}
      data-testid={`tms-card-${tr.id}`}
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span style={mono(11, "#9ec3ec")}>{tr.id}</span>
          <StatusBadge status={tr.status} />
          <span style={mono(9, "#9198b0")}>{tr.type?.toUpperCase()}</span>
          {tr.vehicle_data_deadline_missed && (
            <span style={{ ...mono(9, "#E24B4A", 700), background: "rgba(226,75,74,0.12)", padding: "1px 6px", borderRadius: 3 }} data-testid={`tms-deadline-missed-${tr.id}`}>⚠ DEADLINE MISSED</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpand((p) => !p)}
          className="px-2 py-1 rounded"
          style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.12)", color: "#c5cad8", ...mono(9, undefined, 600, "0.4px"), cursor: "pointer" }}
          data-testid={`tms-card-expand-${tr.id}`}
        >
          {expand ? "↑ Collapse" : "↓ Details"}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" style={mono(11, "var(--text, #f0f2f8)")}>
        <Field label="Route">{formatAddr(tr.pickup_location)} → {formatAddr(tr.delivery_location)}</Field>
        <Field label="Cargo">{tr.cargo_description || "—"}</Field>
        <Field label="Incident">{tr.incident_id} · {tr.incident_title || ""}</Field>
        <Field label="Offers">{tr.offers?.length || 0} {tr.awarded_offer_id ? `· awarded` : ""}</Field>
      </div>

      {(tr.latest_pickup || tr.latest_delivery) && (
        <div className="mt-2 flex flex-wrap gap-3" style={mono(10, "#f3b14a", 600, "0.4px")} data-testid={`tms-timing-${tr.id}`}>
          {tr.latest_pickup && <span>⏱ Latest pickup: {new Date(tr.latest_pickup).toLocaleString()}</span>}
          {tr.latest_delivery && <span>⏱ Latest delivery: {new Date(tr.latest_delivery).toLocaleString()}</span>}
        </div>
      )}

      {tr.status === "agreed" && tr.vehicle_data_deadline_remaining_seconds != null && (
        <CountdownBar tr={tr} />
      )}

      {expand && (
        <div className="mt-3 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          {isOutgoing ? (
            <OutgoingDetail tr={tr} onChange={onChange} addToast={addToast} />
          ) : (
            <IncomingDetail tr={tr} onChange={onChange} addToast={addToast} />
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Outgoing (shipper)
// ---------------------------------------------------------------------------

const OutgoingDetail = ({ tr, onChange, addToast }) => {
  const offered = (tr.offers || []).filter((o) => ["offered", "awarded", "auto_declined", "declined"].includes(o.status));
  const award = async (offerId) => {
    try {
      await axios.patch(`${API}/transport-requests/${tr.id}/award`, { offer_id: offerId });
      addToast({ type: "success", title: "Carrier awarded", body: tr.id, link: { type: "tms", id: tr.id } });
      onChange();
    } catch (err) {
      addToast({ type: "error", title: "Award failed", body: err?.response?.data?.detail || err.message });
    }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this transport request?")) return;
    try {
      await axios.patch(`${API}/transport-requests/${tr.id}/cancel`);
      onChange();
    } catch (err) {
      addToast({ type: "error", title: "Cancel failed", body: err?.response?.data?.detail || err.message });
    }
  };
  const downloadOrder = async () => {
    try {
      const resp = await axios.get(`${API}/transport-requests/${tr.id}/order.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = `${tr.id}-order.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (_) { /* swallow */ }
  };
  return (
    <div data-testid={`tms-outgoing-detail-${tr.id}`}>
      {/* Offers comparison table */}
      <div className="mb-3">
        <div style={mono(9, "#9198b0", 600, "0.8px", true)} className="uppercase mb-1.5">
          Carrier offers — ranked by SLA fit + price
        </div>
        {(tr.offers || []).length === 0 ? (
          <Empty text="Waiting for carrier responses…" testId={`tms-offers-empty-${tr.id}`} />
        ) : (
          <div className="rounded overflow-x-auto" style={{ border: "1px solid rgba(255,255,255,0.10)" }} data-testid={`tms-offers-${tr.id}`}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                  <Th>Carrier</Th>
                  <Th align="right">Rate</Th>
                  <Th>Pickup</Th>
                  <Th align="right">Transit</Th>
                  <Th>Delivery</Th>
                  <Th>Recommendation</Th>
                  <Th>Status</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {tr.offers.map((o) => {
                  const rec = o.recommendation;
                  return (
                    <tr key={o.id} data-testid={`tms-offer-row-${o.id}`} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                      <Td>{o.carrier_name || o.carrier_tenant_id}</Td>
                      <Td align="right">{o.rate != null ? o.rate.toLocaleString() : "—"}</Td>
                      <Td>{o.pickup_time ? new Date(o.pickup_time).toLocaleString() : "—"}</Td>
                      <Td align="right">{o.transit_duration_minutes ? `${o.transit_duration_minutes}m` : "—"}</Td>
                      <Td>{o.delivery_time ? new Date(o.delivery_time).toLocaleString() : "—"}</Td>
                      <Td>
                        {rec ? (
                          <span
                            style={{
                              background: rec.is_top_pick ? "rgba(78,208,132,0.12)" : (rec.meets_sla ? "rgba(24,95,165,0.12)" : "rgba(226,75,74,0.10)"),
                              color: rec.is_top_pick ? "#9aebc2" : (rec.meets_sla ? "#9ec3ec" : "#ff9a99"),
                              padding: "1px 6px",
                              borderRadius: 2,
                              ...mono(9, undefined, 600, "0.3px"),
                            }}
                            data-testid={`tms-offer-rec-${o.id}`}
                          >
                            {rec.label}
                          </span>
                        ) : <span style={mono(9, "#6b7388")}>—</span>}
                      </Td>
                      <Td><StatusBadge status={o.status} /></Td>
                      <Td>
                        {o.status === "offered" && tr.status !== "agreed" && tr.status !== "cancelled" && (
                          <button
                            type="button"
                            onClick={() => award(o.id)}
                            className="px-2.5 py-1 rounded"
                            style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
                            data-testid={`tms-award-btn-${o.id}`}
                          >Award →</button>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Awarded — vehicle data display */}
      {tr.vehicle_data && (
        <div className="mb-3 p-2 rounded" style={{ background: "rgba(78,208,132,0.06)", border: "1px solid rgba(78,208,132,0.25)" }} data-testid={`tms-vehicle-data-${tr.id}`}>
          <div style={mono(9, "#9aebc2", 600, "0.6px", true)} className="uppercase mb-1.5">Vehicle data</div>
          <div className="grid grid-cols-2 gap-2" style={mono(11, "var(--text, #f0f2f8)")}>
            <Field label="Tractor">{tr.vehicle_data.tractor_reg}</Field>
            <Field label="Trailer">{tr.vehicle_data.trailer_reg || "—"}</Field>
            <Field label="Driver">{tr.vehicle_data.driver_name}</Field>
            <Field label="Phone">{tr.vehicle_data.driver_phone || <span style={mono(9, "#6b7388")}>(hidden)</span>}</Field>
          </div>
        </div>
      )}

      <div className="flex gap-2 justify-end">
        {tr.awarded_offer_id && (
          <button
            type="button"
            onClick={downloadOrder}
            className="px-3 py-1.5 rounded"
            style={{ background: "rgba(155,89,182,0.12)", border: "1px solid rgba(155,89,182,0.40)", color: "#c89adb", ...mono(10, undefined, 600, "0.4px"), cursor: "pointer" }}
            data-testid={`tms-order-pdf-${tr.id}`}
          >📥 Transport Order PDF</button>
        )}
        {!["delivered", "cancelled"].includes(tr.status) && (
          <button
            type="button"
            onClick={cancel}
            className="px-3 py-1.5 rounded"
            style={{ background: "transparent", border: "1px solid rgba(226,75,74,0.40)", color: "#ff9a99", ...mono(10, undefined, 600, "0.4px"), cursor: "pointer" }}
            data-testid={`tms-cancel-btn-${tr.id}`}
          >Cancel request</button>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Incoming (carrier)
// ---------------------------------------------------------------------------

const IncomingDetail = ({ tr, onChange, addToast }) => {
  const { user } = useAuth();
  const myCarrierId = user?.tenant_id;
  const myOffer = (tr.offers || []).find((o) => o.carrier_tenant_id === myCarrierId);
  const isAwardedCarrier = tr.awarded_carrier_tenant_id === myCarrierId;
  const [rate, setRate] = useState("");
  const [pickup, setPickup] = useState("");
  const [transit, setTransit] = useState("");
  const [delivery, setDelivery] = useState("");
  const [note, setNote] = useState("");
  const [vd, setVd] = useState({ tractor: "", trailer: "", driver: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const submitOffer = async () => {
    if (!rate || !pickup || !transit || !delivery) {
      addToast({ type: "error", title: "Fill rate + pickup + transit + delivery" });
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`${API}/transport-requests/${tr.id}/respond`, {
        rate: parseFloat(rate),
        pickup_time: new Date(pickup).toISOString(),
        transit_duration_minutes: parseInt(transit, 10),
        delivery_time: new Date(delivery).toISOString(),
        note: note || null,
      });
      addToast({ type: "success", title: "Offer submitted", body: tr.id });
      onChange();
    } catch (err) {
      addToast({ type: "error", title: "Offer failed", body: err?.response?.data?.detail || err.message });
    } finally { setBusy(false); }
  };
  const decline = async () => {
    const reason = window.prompt("Decline reason (optional)") || null;
    try {
      await axios.patch(`${API}/transport-requests/${tr.id}/decline`, { reason });
      onChange();
    } catch (err) { /* swallow */ }
  };
  const submitVd = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/transport-requests/${tr.id}/vehicle-data`, {
        tractor_reg: vd.tractor, trailer_reg: vd.trailer || null,
        driver_name: vd.driver, driver_phone: vd.phone,
      });
      addToast({ type: "success", title: "Vehicle data submitted" });
      onChange();
    } catch (err) {
      addToast({ type: "error", title: "Vehicle data failed", body: err?.response?.data?.detail || err.message });
    } finally { setBusy(false); }
  };
  const confirm = async (stage) => {
    try {
      await axios.patch(`${API}/transport-requests/${tr.id}/confirm-${stage}`, {});
      addToast({ type: "success", title: stage === "pickup" ? "Pickup confirmed" : "Delivery confirmed" });
      onChange();
    } catch (err) {
      addToast({ type: "error", title: `${stage} failed`, body: err?.response?.data?.detail || err.message });
    }
  };

  return (
    <div data-testid={`tms-incoming-detail-${tr.id}`}>
      {/* Iter43-B — Address details + timing requirements visible to carrier */}
      <div className="grid grid-cols-2 gap-3 mb-3" data-testid={`tms-requirements-${tr.id}`}>
        <div className="p-2 rounded" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
          <div style={mono(9, "#9198b0", 600, "0.6px", true)} className="uppercase mb-1.5">Pickup</div>
          <div style={mono(11, "var(--text, #f0f2f8)")}>{formatAddr(tr.pickup_location)}</div>
          {tr.latest_pickup && <div className="mt-1" style={mono(10, "#f3b14a", 600)}>Latest: {new Date(tr.latest_pickup).toLocaleString()}</div>}
        </div>
        <div className="p-2 rounded" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
          <div style={mono(9, "#9198b0", 600, "0.6px", true)} className="uppercase mb-1.5">Delivery</div>
          <div style={mono(11, "var(--text, #f0f2f8)")}>{formatAddr(tr.delivery_location)}</div>
          {tr.latest_delivery && <div className="mt-1" style={mono(10, "#f3b14a", 600)}>Latest: {new Date(tr.latest_delivery).toLocaleString()}</div>}
        </div>
      </div>

      {/* Offer submission */}
      {myOffer?.status === "pending" && tr.status !== "cancelled" && (
        <div className="mb-3" data-testid={`tms-respond-form-${tr.id}`}>
          <div style={mono(9, "#9198b0", 600, "0.8px", true)} className="uppercase mb-1.5">Submit your offer</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
            <FormInput label="Rate" type="number" value={rate} onChange={setRate} testId={`tms-rate-${tr.id}`} />
            <FormInput label="Pickup time" type="datetime-local" value={pickup} onChange={setPickup} testId={`tms-pickup-${tr.id}`} />
            <FormInput label="Transit (min)" type="number" value={transit} onChange={setTransit} testId={`tms-transit-${tr.id}`} />
            <FormInput label="Delivery time" type="datetime-local" value={delivery} onChange={setDelivery} testId={`tms-delivery-${tr.id}`} />
          </div>
          {/* SLA-miss warning: carrier's committed delivery_time exceeds latest_delivery */}
          {tr.latest_delivery && delivery && new Date(delivery) > new Date(tr.latest_delivery) && (
            <div
              className="rounded px-3 py-2 mb-2"
              style={{ background: "rgba(226,75,74,0.12)", border: "1px solid rgba(226,75,74,0.45)", color: "#ff9a99", ...mono(11, undefined, 600) }}
              data-testid={`tms-sla-miss-warning-${tr.id}`}
            >
              ⚠ Your delivery time is after the shipper's hard requirement
              ({new Date(tr.latest_delivery).toLocaleString()}). The offer will rank as
              "Misses latest delivery" — submit at your own risk.
            </div>
          )}
          <FormInput label="Note (optional)" value={note} onChange={setNote} testId={`tms-note-${tr.id}`} />
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={decline} className="px-3 py-1.5 rounded"
              style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", ...mono(10, undefined, 600, "0.4px"), cursor: "pointer" }}
              data-testid={`tms-decline-btn-${tr.id}`}>Decline</button>
            <button type="button" onClick={submitOffer} disabled={busy} className="px-3 py-1.5 rounded"
              style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}
              data-testid={`tms-submit-offer-btn-${tr.id}`}>Submit offer</button>
          </div>
        </div>
      )}

      {/* If my offer is offered/awarded, show summary */}
      {myOffer && ["offered", "awarded", "auto_declined", "declined"].includes(myOffer.status) && (
        <div className="mb-3 p-2 rounded" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`tms-my-offer-${tr.id}`}>
          <div style={mono(9, "#9198b0", 600, "0.6px", true)} className="uppercase mb-1.5">Your offer</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" style={mono(11, "var(--text, #f0f2f8)")}>
            <Field label="Status"><StatusBadge status={myOffer.status} /></Field>
            <Field label="Rate">{myOffer.rate}</Field>
            <Field label="Pickup">{myOffer.pickup_time ? new Date(myOffer.pickup_time).toLocaleString() : "—"}</Field>
            <Field label="Delivery">{myOffer.delivery_time ? new Date(myOffer.delivery_time).toLocaleString() : "—"}</Field>
          </div>
        </div>
      )}

      {/* Awarded → vehicle data, then pickup/delivery */}
      {isAwardedCarrier && tr.status === "agreed" && !tr.vehicle_data && (
        <div className="mb-3" data-testid={`tms-vehicle-form-${tr.id}`}>
          <div style={mono(9, "#9198b0", 600, "0.8px", true)} className="uppercase mb-1.5">
            ⏱ Vehicle data — within {tr.vehicle_data_deadline_minutes} min of award
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
            <FormInput label="Tractor reg" value={vd.tractor} onChange={(v) => setVd((p) => ({ ...p, tractor: v }))} testId={`tms-vd-tractor-${tr.id}`} />
            <FormInput label="Trailer reg" value={vd.trailer} onChange={(v) => setVd((p) => ({ ...p, trailer: v }))} testId={`tms-vd-trailer-${tr.id}`} />
            <FormInput label="Driver name" value={vd.driver} onChange={(v) => setVd((p) => ({ ...p, driver: v }))} testId={`tms-vd-driver-${tr.id}`} />
            <FormInput label="Driver phone" value={vd.phone} onChange={(v) => setVd((p) => ({ ...p, phone: v }))} testId={`tms-vd-phone-${tr.id}`} />
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={submitVd} disabled={busy} className="px-3 py-1.5 rounded"
              style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: busy ? "wait" : "pointer" }}
              data-testid={`tms-vd-submit-${tr.id}`}>Submit vehicle data</button>
          </div>
        </div>
      )}

      {isAwardedCarrier && tr.status === "dispatched" && (
        <div className="flex justify-end">
          <button type="button" onClick={() => confirm("pickup")} className="px-3 py-1.5 rounded"
            style={{ background: "#4ed084", color: "#0a1620", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
            data-testid={`tms-confirm-pickup-${tr.id}`}>✓ Confirm pickup</button>
        </div>
      )}
      {isAwardedCarrier && tr.status === "in_transit" && (
        <div className="flex justify-end">
          <button type="button" onClick={() => confirm("delivery")} className="px-3 py-1.5 rounded"
            style={{ background: "#4ed084", color: "#0a1620", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
            data-testid={`tms-confirm-delivery-${tr.id}`}>✓ Confirm delivery</button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CountdownBar = ({ tr }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const initial = (tr.vehicle_data_deadline_minutes || 60) * 60;
  // Note: the backend already sends remaining_seconds, but it's stale by the
  // refresh interval. Approximate by recomputing locally.
  const deadline = useMemo(() => {
    try { return new Date(tr.vehicle_data_deadline).getTime(); } catch { return null; }
  }, [tr.vehicle_data_deadline]);
  const remainingMs = deadline ? deadline - now : null;
  const remaining = remainingMs != null ? Math.max(0, Math.floor(remainingMs / 1000)) : null;
  const pct = remaining != null ? Math.max(0, Math.min(100, (remaining / initial) * 100)) : 0;
  const color = remaining == null || remaining > initial * 0.25 ? "#4ed084" : remaining > 0 ? "#f3b14a" : "#E24B4A";
  const mins = remaining != null ? Math.floor(remaining / 60) : null;
  const secs = remaining != null ? remaining % 60 : null;
  return (
    <div className="mt-2.5" data-testid={`tms-countdown-${tr.id}`}>
      <div className="flex items-center justify-between mb-1">
        <span style={mono(9, "#9198b0", 600, "0.6px", true)} className="uppercase">⏱ Vehicle data deadline</span>
        <span style={mono(11, color, 700)} data-testid={`tms-countdown-value-${tr.id}`}>
          {mins != null ? `${mins}:${String(secs).padStart(2, "0")}` : "—"}
        </span>
      </div>
      <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, transition: "width 1s linear, background-color 0.5s" }} />
      </div>
    </div>
  );
};

const Tab = ({ active, onClick, testId, label }) => (
  <button type="button" onClick={onClick}
    className="px-3 py-1.5 rounded"
    style={{
      background: active ? "rgba(24,95,165,0.16)" : "transparent",
      border: `1px solid ${active ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
      color: active ? "#9ec3ec" : "var(--text-dim, #9aa1b3)",
      ...mono(11, undefined, 600, "0.5px"),
      cursor: "pointer",
    }}
    data-testid={testId}
    aria-pressed={active}
  >{label}</button>
);

const StatusBadge = ({ status }) => {
  const bg = STATUS_COLOR[status] || "#9aa1b3";
  return (
    <span style={{
      background: `${bg}28`, color: bg,
      padding: "1px 6px", borderRadius: 2,
      ...mono(9, undefined, 600, "0.5px"),
      textTransform: "uppercase",
    }}>{(status || "").replace(/_/g, " ")}</span>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div style={mono(8, "#9198b0", 500, "1.2px", true)} className="uppercase">{label}</div>
    <div className="mt-0.5" style={mono(11, "var(--text, #f0f2f8)")}>{children}</div>
  </div>
);

const FormInput = ({ label, value, onChange, type = "text", testId }) => (
  <div>
    <div style={mono(8, "#9198b0", 500, "1.2px", true)} className="uppercase mb-0.5">{label}</div>
    <input
      type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 rounded outline-none"
      style={{ background: "var(--bg3, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11) }}
      data-testid={testId}
    />
  </div>
);

const Th = ({ children, align }) => (
  <th style={{ ...mono(9, "#9198b0", 600, "0.8px"), textAlign: align || "left", padding: "5px 8px", textTransform: "uppercase" }}>{children}</th>
);
const Td = ({ children, align }) => (
  <td style={{ ...mono(11, "var(--text, #f0f2f8)"), textAlign: align || "left", padding: "5px 8px", verticalAlign: "middle" }}>{children}</td>
);

const Empty = ({ text, hint, ctaLabel, ctaOnClick, testId }) => (
  <div className="rounded-md p-8 text-center"
    style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.08)" }}
    data-testid={testId}>
    <div style={mono(13, "var(--text-dim, #c5cad8)", 600)}>{text}</div>
    {hint && <div className="mt-1.5" style={mono(10, "#9198b0")}>{hint}</div>}
    {ctaLabel && (
      <button
        type="button"
        onClick={ctaOnClick}
        className="mt-3 px-3 py-1.5 rounded"
        style={{ background: "#185FA5", color: "#fff", ...mono(11, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
        data-testid={`${testId}-cta`}
      >{ctaLabel}</button>
    )}
  </div>
);

const mono = (size, color, weight, ls, _uppercase) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
